import { FC, memo, useEffect, useState } from "react";
import * as React from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { ArrowLeft as ArrowLeftIcon } from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { NotFound } from "@hl/shared-features/lib/features/layout";
import { AppSEO } from "@hl/shared-features/lib/features/layout/AppSEO";
import useBeforeunload from "@hl/shared-features/lib/hooks/useBeforeunload";
import { usePrompt } from "@hl/shared-features/lib/hooks/usePrompt";
import {
  AppShell,
  Button,
  Container,
  Grid,
  Space,
  Stack,
  Title,
} from "@mantine/core";
import { Link, useNavigate, useParams } from "react-router-dom";

import { TOOLS_URL } from "config";
import { AppFooter } from "~/features/layout/Footer";
import { AppHeader } from "~/features/layout/Header";

import { CreateOrEditGate } from "./CreateOrEditGate";
import GatedEntities from "./GatedEntities";
import { useGateQuery } from "./queries/gates.graphql.generated";

const EditGate: FC = () => {
  const navigate = useNavigate();
  const { gateId } = useParams();
  const [isChanged, setChanged] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data, loading } = useGateQuery({
    variables: {
      id: gateId ?? "",
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (saved) {
      navigate(TOOLS_URL.gates.list);
    }
  }, [saved]);

  usePrompt("You have unsaved changes. Discard?", isChanged);
  useBeforeunload((event) => {
    if (isChanged) {
      event.preventDefault();
    }
  });

  if (loading) {
    return <LoadingPage />;
  }

  if (!data) {
    return <NotFound />;
  }

  return (
    <>
      <AppSEO title={data?.gate?.name} />
      <AppShell
        padding="md"
        header={
          <AppHeader
            leftButtons={
              <Link to={TOOLS_URL.gates.list}>
                <Button
                  color={SECONDARY_COLOR}
                  leftIcon={<ArrowLeftIcon height={12} />}
                >
                  Back
                </Button>
              </Link>
            }
          />
        }
        footer={<AppFooter />}
      >
        <Container size="md" mb={116}>
          <Grid gutter={80}>
            <Grid.Col span={8}>
              <Stack spacing={0}>
                <Title order={4}>Edit gate</Title>
                <Space h={40} />
                <CreateOrEditGate
                  gate={data?.gate}
                  onSave={() => {
                    setChanged(false);
                    setSaved(true);
                  }}
                  onChange={() => setChanged(true)}
                />
                <Space h={24} />
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              <GatedEntities gateId={data?.gate.id} />
            </Grid.Col>
          </Grid>
        </Container>
      </AppShell>
    </>
  );
};

export default memo(EditGate);
